import axios from "axios";
import store from "../store";
import env from "../env";
import setAuthToken from "./setAuthToken";
import { LOGIN_SUCCESS } from "../action/types";
import { readStoredAuthInfo, withExpiresFromPayload } from "./sessionAuth";

/** API path for UM token refresh across environments (base URL comes from tenant/env). */
export const USER_MANAGEMENT_TOKEN_REFRESH_PATH =
  "/simplifi/v1/userManagement/tokenRefresh";

let refreshInflight = null;

/** Clears persisted auth slices (does not navigate). */
export const clearStoredAuthSlices = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("userInfo");
  localStorage.removeItem("authInfo");
  localStorage.removeItem("userRoles");
  localStorage.removeItem("userLevel");
  localStorage.removeItem("loggedInUserName");
  setAuthToken();
};

/**
 * Uses refresh token to obtain a new access token. Updates Redux + authInfo + axios headers.
 * @returns {Promise<string|null>} New access token, or null if no refresh token or refresh failed.
 */
export const refreshAccessToken = async () => {
  const auth = readStoredAuthInfo();
  if (!auth?.refreshToken) return null;

  if (!refreshInflight) {
    const umURL = env.GET_USERMANAGEMENT_API_URL();
    const url = `${umURL}${USER_MANAGEMENT_TOKEN_REFRESH_PATH}`;
    refreshInflight = (async () => {
      try {
        const clientId = env.GET_CLIENT_ID();
        if (!clientId) {
          throw new Error("Refresh token requires clientId");
        }
        const res = await axios.post(
          url,
          {
            token: auth.refreshToken,
            clientId,
          },
          {
            skipSessionHandlers: true,
          }
        );
        const data = res?.data?.data ?? res?.data ?? {};
        const accessToken = data.accessToken;
        const refreshToken = data.refreshToken ?? auth.refreshToken;
        if (!accessToken) {
          throw new Error("Refresh response missing accessToken");
        }
        const current = readStoredAuthInfo() || auth;
        let next = {
          ...current,
          accessToken,
          refreshToken,
          expiresIn: data.expiresIn ?? current.expiresIn,
          userId: data.userId ?? current.userId,
        };
        next = withExpiresFromPayload(next);
        localStorage.setItem("authInfo", JSON.stringify(next));
        setAuthToken(accessToken);
        store.dispatch({ type: LOGIN_SUCCESS, payload: next });
        return accessToken;
      } catch {
        clearStoredAuthSlices();
        return null;
      } finally {
        refreshInflight = null;
      }
    })();
  }

  return refreshInflight;
};
